import React from 'react';
import { Grid } from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import DyanamicDropdown from 'components/Elements/DynamicDropdown';
import { useFilters } from '../FilterContext';
import { useDepartments } from './hooks/useDepartments';
import { useFilterInitialization } from './hooks/useFilterInitialization';
import { SHOW_ALL_OPTION } from '../constants';

export const DepartmentFilter = () => {
  const { filterValues, updateFilter, isShowAll, config, initialValues } = useFilters();

  const me = JSON.parse(localStorage.getItem("me")) ||
      JSON.parse(sessionStorage.getItem("me")) ||
      {};
  const { siteAppID, customCaptions } = me;
  const { siteDepartmentID, siteDepartmentName } = me.site ?? {};

  const multiSelect = config.filters?.department?.multiSelect || false;
  const filterKey = 'department';
  const departments = useDepartments(siteAppID, isShowAll(filterKey));

  useFilterInitialization(filterKey, departments, {
    waitForData: true,
    persistAll: true,
    multiSelect,
    getDefaultValue: () => {
      // Check if 'all' is stored in initialValues for multi-select
      const storedValue = initialValues?.department;

      if (multiSelect) {
        // If 'all' is stored, use the show all option
        if (storedValue === 'all') {
          return [SHOW_ALL_OPTION];
        }
        // If array is stored (from URL or localStorage), let useFilterInitialization handle it
        if (Array.isArray(storedValue) && storedValue.length > 0) {
          return []; // Return empty array to let useFilterInitialization restore from stored IDs
        }
        // Only use default when there's truly NO stored value
        const userDepartment = siteDepartmentID ? { id: siteDepartmentID, name: siteDepartmentName } : null;
        return userDepartment ? [userDepartment] : [SHOW_ALL_OPTION];
      } else {
        // For single select, handle 'all' the same way
        if (storedValue === 'all') {
          return SHOW_ALL_OPTION;
        }
        // If there's a stored value, let useFilterInitialization handle it
        if (storedValue) {
          return null; // Return null to let useFilterInitialization restore from stored ID
        }
        // Only use default when there's truly NO stored value
        const userDepartment = siteDepartmentID ? { id: siteDepartmentID, name: siteDepartmentName } : null;
        return userDepartment || SHOW_ALL_OPTION;
      }
    },
    getParams: (value) => {
      if (multiSelect) {
        if (!value || value.length === 0) return {};
        const hasShowAll = value.some(dept => dept.id === '' || dept.id === 'all');
        if (hasShowAll) return {};
        const departmentIds = value.map(dept => dept.id).filter(id => id !== '' && id != null);
        return departmentIds.length === 0 ? {} : { siteDepartmentIds: departmentIds };
      }
      return value?.id && value.id !== '' ? { siteDepartmentId: value.id } : {};
    },
    getReportParams: (value) => {
      if (multiSelect) {
        if (!value || value.length === 0) return {};
        const hasShowAll = value.some(dept => dept.id === '' || dept.id === 'all');
        if (hasShowAll) return {};
        const ids = value.map(dept => dept.id).filter(id => id !== '' && id != null);
        return ids.length === 0 ? {} : { siteDepartmentIds: ids };
      }
      return value?.id && value.id !== '' ? { siteDepartmentIds: [value.id] } : {};
    },
  });

  const handleChange = (selection) => {
    let params;
    let reportParams;
    if (multiSelect) {
      const hasShowAll = selection.some(dept => dept.id === '' || dept.id === 'all');
      if (hasShowAll) {
        params = {};
        reportParams = {};
      } else {
        const ids = selection.map(dept => dept.id).filter(id => id !== '' && id != null);
        params = ids.length === 0 ? {} : { siteDepartmentIds: ids };
        reportParams = ids.length === 0 ? {} : { siteDepartmentIds: ids };
      }
    } else {
      params = selection?.id && selection.id !== '' ? { siteDepartmentId: selection.id } : {};
      reportParams = selection?.id && selection.id !== '' ? { siteDepartmentIds: [selection.id] } : {};
    }
    updateFilter(filterKey, selection, params, true, reportParams);
  };

  const getPlaceholder = () => {
    if (!multiSelect) return `Select ${customCaptions?.department || 'Department'}`;
    const selected = filterValues[filterKey]?.value || [];
    return selected.length === 0
      ? 'Show All'
      : `${selected.length} ${customCaptions?.department || 'Department'}(s) selected`;
  };

  return (
    <Grid item lg={3} md={6} xs={12}>
      <DyanamicDropdown
        dataSource={departments}
        columns={[{ name: 'name', id: 1 }]}
        showHeader={false}
        width="100%"
        placeholder={getPlaceholder()}
        onChange={handleChange}
        selectdValueToshow="name"
        selectedValue={multiSelect ? undefined : filterValues[filterKey]?.value}
        selectedValues={multiSelect ? filterValues[filterKey]?.value : undefined}
        label={`Filter by ${customCaptions?.department || 'Department'}`}
        icon={<FilterListIcon style={{ color: 'rgb(48,122,215)' }} />}
        required={false}
        showBorderColor
        multiSelect={multiSelect}
        maxDisplayItems={2}
        showSearchBox={true}
      />
    </Grid>
  );
}; 
